import { Download } from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";

import { DisplayRow, UploadResult } from "./types";
import { downloadFailedCSV } from "./utils";

interface ResultsPhaseProps {
  userCreationResults: UploadResult[];
  permissionResults: UploadResult[];
  existingRows: DisplayRow[];
}

const FailureList = ({ results }: { results: UploadResult[] }) => (
  <Box
    sx={{
      maxHeight: 300,
      overflow: "auto",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
    }}
  >
    {results.map((r, i) => (
      <Box
        key={i}
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          gap: 2,
          alignItems: "baseline",
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: 0 },
        }}
      >
        <Typography variant="body2" sx={{ minWidth: 220, flexShrink: 0 }}>
          {r.row.first_name} {r.row.last_name}{" "}
          <Typography component="span" variant="body2" color="text.secondary">
            ({r.row.email})
          </Typography>
        </Typography>
        <Typography variant="body2" color="error.main">
          {r.error}
        </Typography>
      </Box>
    ))}
  </Box>
);

export const ResultsPhase = ({
  userCreationResults,
  permissionResults,
  existingRows,
}: ResultsPhaseProps) => {
  const creationSuccessCount = userCreationResults.filter(
    (r) => !r.error,
  ).length;
  const creationFailCount = userCreationResults.filter((r) => r.error).length;
  const permissionSuccessCount = permissionResults.filter(
    (r) => !r.error,
  ).length;
  const permissionFailCount = permissionResults.filter((r) => r.error).length;

  const failedCreations = userCreationResults.filter((r) => r.error);
  const failedPermissions = permissionResults.filter((r) => r.error);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Step 1 summary */}
      {userCreationResults.length > 0 ? (
        <Alert severity={creationFailCount === 0 ? "success" : "warning"}>
          <strong>Step 1 — User creation:</strong>{" "}
          <strong>{creationSuccessCount}</strong> user
          {creationSuccessCount !== 1 ? "s" : ""} created
          {creationFailCount > 0 && (
            <>
              , <strong>{creationFailCount}</strong> failed
            </>
          )}
          .
          {existingRows.length > 0 && (
            <>
              {" "}
              {existingRows.length} existing user
              {existingRows.length !== 1 ? "s" : ""} skipped.
            </>
          )}
        </Alert>
      ) : (
        <Alert severity="info">
          <strong>Step 1 — User creation:</strong> No new users to create
          {existingRows.length > 0 && (
            <>
              {" "}
              — {existingRows.length} existing user
              {existingRows.length !== 1 ? "s" : ""} in CSV
            </>
          )}
          .
        </Alert>
      )}

      {/* Step 2 summary */}
      <Alert severity={permissionFailCount === 0 ? "success" : "warning"}>
        <strong>Step 2 — Permissions:</strong>{" "}
        {permissionResults.length === 0 ? (
          "No permissions to apply."
        ) : (
          <>
            <strong>{permissionSuccessCount}</strong> user
            {permissionSuccessCount !== 1 ? "s" : ""} updated
            {permissionFailCount > 0 && (
              <>
                , <strong>{permissionFailCount}</strong> failed
              </>
            )}
            .
          </>
        )}
      </Alert>

      {failedPermissions.length > 0 && (
        <>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => downloadFailedCSV(permissionResults)}
            sx={{ alignSelf: "flex-start" }}
          >
            Download failed records CSV
          </Button>
          <FailureList results={failedPermissions} />
        </>
      )}

      {failedCreations.length > 0 && (
        <>
          <Typography variant="body2" color="text.secondary">
            Failed to create:
          </Typography>
          <FailureList results={failedCreations} />
        </>
      )}
    </Box>
  );
};
